import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetIndividualViewDocument } from "~/graphql/supergraphHooks";
import {
  type GetIndividualViewQuery,
  type IndividualViewSortInput,
  type IndividualViewSortField,
  type SortDirection,
} from "~/graphql/supergraphTypes";
import { INDIVIDUALS_TABLE_PAGE_SIZE } from "./gridOptions";
import { IndividualRowItem, IndividualsData, DateRangeMetadata } from "./types";

const getSortVars = (
  sortModel: AgGrid.SortModelItem[] | null,
): IndividualViewSortInput | null => {
  if (!sortModel || sortModel.length === 0) {
    return null;
  }

  const firstSort = sortModel[0];
  const fieldMap: Record<string, IndividualViewSortField> = {
    assigneeName: "ASSIGNEE_NAME",
    activeWorkflows: "ACTIVE_WORKFLOWS",
    due02Days: "DUE_0_2_DAYS",
    due35Days: "DUE_3_5_DAYS",
    due614Days: "DUE_6_14_DAYS",
    due15PlusDays: "DUE_15_PLUS_DAYS",
  };

  const field = fieldMap[firstSort.colId];
  if (!field) {
    return null;
  }

  const direction: SortDirection = firstSort.sort === "asc" ? "ASC" : "DESC";

  return {
    field,
    direction,
  };
};

export const rowsFromQuery = (
  individuals: IndividualsData,
  dateRangeMetadata?: DateRangeMetadata | null,
  activeStatuses?: string[] | null,
): IndividualRowItem[] => {
  return individuals.map((individual) => ({
    assigneeId: individual.assigneeId,
    assigneeName: individual.assigneeName,
    assigneeEmail: individual.assigneeEmail ?? null,
    activeWorkflows: individual.activeWorkflows,
    workflowTypes: individual.workflowTypes ?? [],
    due02Days: individual.due02Days,
    due35Days: individual.due35Days,
    due614Days: individual.due614Days,
    due15PlusDays: individual.due15PlusDays,
    dateRangeMetadata,
    activeStatuses,
  }));
};

export class IndividualsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<NormalizedCacheObject>;
  private pageSize: number = INDIVIDUALS_TABLE_PAGE_SIZE;

  constructor(apolloClient: ApolloClient<NormalizedCacheObject> = client) {
    this.client = apolloClient;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetIndividualViewQuery,
  ): void {
    if (!response.individualView) {
      this.handleError(params);
      return;
    }

    const { rows, pageInfo, dateRangeMetadata, activeStatuses } = response.individualView;
    const rowData = rowsFromQuery(rows, dateRangeMetadata, activeStatuses);

    const rowCount = pageInfo.totalCount;

    params.success({
      rowData,
      rowCount,
    });

    if (rowData.length === 0) {
      params.api?.showNoRowsOverlay();
    } else {
      params.api?.hideOverlay();
    }
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? startRow + this.pageSize;
    const limit = Math.max(endRow - startRow, this.pageSize);
    const page = Math.floor(startRow / limit) + 1;
    const sorts = getSortVars(params.request.sortModel);

    this.client
      .query<GetIndividualViewQuery>({
        query: GetIndividualViewDocument,
        variables: {
          sort: sorts,
          limit,
          page,
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        this.handleSuccess(params, response.data);
      })
      .catch(() => {
        this.handleError(params);
      });
  }
}
